import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { map } from 'lit/directives/map.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import type { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpPremiumBundle, PdpPremiumBundleProduct, PdpPremiumBundles } from 'components/discovery/pdp/typing/pdp-product.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { formatGiftPrice } from 'components/discovery/pdp/shared/format-gift-price.js'
import 'components/shared/product-preview/product-preview.js'
import { style } from './premium-free-product.style.js'

export const premiumFreeProductHiddenFields = ['title', 'badge', 'description', 'image', 'original-price'] as const

type PaperVariant = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
type HeadingVariant = 'heading-1' | 'heading-2' | 'heading-3' | 'heading-4' | 'heading-5' | 'heading-6'
type BadgeVariant = 'default' | 'error' | 'info' | 'success' | 'warning' | 'new' | 'our-choice' | 'bestseller' | 'sale' | 'premium'

const headingTagByVariant: Record<HeadingVariant, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'heading-5': 'h5',
  'heading-6': 'h6',
}

@customElement('csc-premium-free-product')
export class CSCPremiumFreeProduct extends HiddenFieldsMixin(LitElement, premiumFreeProductHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: String, attribute: 'paper-variant' })
  paperVariant: PaperVariant = 'primary'

  @property({ type: String, attribute: 'heading-variant' })
  headingVariant: HeadingVariant = 'heading-4'

  @property({ type: String, attribute: 'badge-variant' })
  badgeVariant: BadgeVariant = 'sale'

  @property()
  locale = 'en-US'

  @state()
  private premiumBundles: PdpPremiumBundles | null = null

  @query('cse-slide-in')
  private previewSlideIn?: CSESlideIn

  private previewing: Promise<void> = Promise.resolve()

  constructor() {
    super()
    createEffect(() => {
      this.premiumBundles = this.store.product()?.premium_bundles ?? null
    })
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete()
    await this.previewing
    return result
  }

  private openPreview = async (): Promise<void> => {
    this.previewing = this.previewSlideIn?.open() ?? Promise.resolve()
    await this.previewing
  }

  private closePreview = async (): Promise<void> => {
    this.previewing = this.previewSlideIn?.close() ?? Promise.resolve()
    await this.previewing
  }

  private get validHeadingVariant(): HeadingVariant {
    return Object.hasOwn(headingTagByVariant, this.headingVariant) ? this.headingVariant : 'heading-4'
  }

  private renderHeader(name: string) {
    if (this.isHidden('badge') && this.isHidden('title')) return nothing

    return html`
      <cse-group direction="row" alignment="center" column-gap="05">
        ${!this.isHidden('badge') ? html`
          <cse-badge variant="${this.badgeVariant}" composition="solid">
            ${t('badge', { ns: 'csc-premium-free-product', default: 'Free' })}
          </cse-badge>
        ` : nothing}
        ${!this.isHidden('title') ? html`
          <cse-text as="${headingTagByVariant[this.validHeadingVariant]}" variant="${this.validHeadingVariant}">${name}</cse-text>
        ` : nothing}
      </cse-group>
    `
  }

  private renderGiftPreview = (gift: PdpPremiumBundleProduct) => html`
    <csc-product-preview
      product-name="${gift.name}"
      product-id="${gift.code}"
      image-source="${gift.picture.small}"
      hidden-fields="${this.isHidden('image') ? 'image' : ''}"
      price-variant="sale"
      price-lead="${this.isHidden('original-price') ? '' : formatGiftPrice(gift, this.locale)}"
      price="${t('price', { ns: 'csc-premium-free-product', default: 'Free' })}"
    ></csc-product-preview>
  `

  private renderGift = (gift: PdpPremiumBundleProduct) => html`
    <cse-list-item>${this.renderGiftPreview(gift)}</cse-list-item>
  `

  private renderPreviewGift = (gift: PdpPremiumBundleProduct) => html`
    <cse-paper variant="primary" bordered>${this.renderGiftPreview(gift)}</cse-paper>
  `

  private renderGifts(products: PdpPremiumBundleProduct[]) {
    return html`
      <cse-divider orientation="horizontal" variant="dashed"></cse-divider>
      <cse-list divider>
        ${map(products, this.renderGift)}
      </cse-list>
    `
  }

  private renderDescription(description: string) {
    if (this.isHidden('description') || !description) return nothing

    return html`<cse-text variant="body">${description}</cse-text>`
  }

  private renderPreviewButton() {
    return html`
      <div>
        <cse-button class="csc-premium-free-product__view" size="small" @click="${this.openPreview}">
          ${t('view_free_products', { ns: 'csc-premium-free-product', default: 'View free products' })}
        </cse-button>
      </div>
    `
  }

  private renderPreviewSlideIn(bundle: PdpPremiumBundle) {
    return html`
      <cse-slide-in>
        <cse-text slot="header" as="h4" variant="heading-4">${bundle.name}</cse-text>
        <cse-group direction="column" row-gap="06">
          ${this.renderDescription(bundle.description)}
          <cse-group direction="column" row-gap="04">
            ${map(bundle.products, this.renderPreviewGift)}
          </cse-group>
        </cse-group>
        <cse-button slot="footer" fit="fill" @click="${this.closePreview}">
          ${t('close', { ns: 'csc-premium-free-product', default: 'Close' })}
        </cse-button>
      </cse-slide-in>
    `
  }

  override render() {
    const bundle = this.premiumBundles?.premium_bundle
    if (!bundle || bundle.products.length === 0) return nothing

    const chooseOneGift = Boolean(this.premiumBundles?.choose_sub_product)

    return html`
      <cse-paper class="csc-premium-free-product" variant="${this.paperVariant}" bordered>
        ${this.renderHeader(bundle.name)}
        <cse-group direction="column" row-gap="${chooseOneGift ? '05' : '06'}">
          ${this.renderDescription(bundle.description)}
          ${chooseOneGift ? this.renderPreviewButton() : this.renderGifts(bundle.products)}
        </cse-group>
      </cse-paper>
      ${chooseOneGift ? this.renderPreviewSlideIn(bundle) : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-premium-free-product': CSCPremiumFreeProduct
  }
}
